// Powers the homepage hero slider (sections/hero-slider.liquid).
// The track is a native horizontal scroller with scroll-snap, so touch swipe
// works with no JS. This script adds:
//  - live dot sync: dots follow the scroll position every animation frame
//    (no debounce), so they react the moment a swipe/drag/autoplay moves
//  - snappy desktop mouse drag: while dragging, smooth scrolling and snap
//    are switched off so the slide follows the cursor 1:1; on release it
//    settles on the next/previous slide based on drag distance OR flick
//    velocity, then snap is restored
//  - dot clicks / autoplay use a smooth scroll and light the target dot
//    immediately
//  - autoplay that pauses on hover, touch, drag, hidden tab and reduced motion
//  - after first paint, the remaining slide images are switched to eager so
//    they are already decoded when the user swipes to them
// RTL (Arabic): scrollLeft runs 0 -> negative, so positions use Math.abs and
// drag directions are mirrored.
(function () {
  var DRAG_THRESHOLD = 6;      // px before a mouse press counts as a drag
  var SWIPE_RATIO = 0.12;      // fraction of slide width that commits a slide change
  var FLICK_VELOCITY = 0.35;   // px/ms that commits a slide change regardless of distance

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function initSlider(root) {
    if (root.dataset.heroSliderInitialized === 'true') return;

    var track = root.querySelector('[data-hero-slider-track]');
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-hero-slider-dot]'));
    if (!track) return;

    var slides = Array.prototype.slice.call(track.children);
    if (!slides.length) return;

    root.dataset.heroSliderInitialized = 'true';

    var isRtl = getComputedStyle(track).direction === 'rtl';
    var speed = (parseInt(root.dataset.autoplaySpeed, 10) || 5) * 1000;
    var autoplayAllowed = root.dataset.autoplay !== 'false' && slides.length > 1;

    var current = 0;
    var timer = null;
    var hovering = false;
    var dragging = false;
    var programmaticTarget = -1;
    var programmaticTimer = null;
    var rafPending = false;

    function width() {
      return track.clientWidth || 1;
    }

    function position() {
      return Math.abs(track.scrollLeft);
    }

    function setActiveDot(index) {
      dots.forEach(function (dot, i) {
        var active = i === index;
        if (dot.classList.contains('bg-primary') === active) return;
        dot.classList.toggle('bg-primary', active);
        dot.classList.toggle('w-6', active);
        dot.classList.toggle('md:w-8', active);
        dot.classList.toggle('bg-transparent', !active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    function scrollToIndex(index, smooth) {
      var left = index * width() * (isRtl ? -1 : 1);
      var behavior = (smooth === false || prefersReducedMotion()) ? 'auto' : 'smooth';
      track.scrollTo({ left: left, behavior: behavior });
    }

    function goTo(index, smooth) {
      current = (index + slides.length) % slides.length;
      programmaticTarget = current;
      setActiveDot(current);
      scrollToIndex(current, smooth);
      window.clearTimeout(programmaticTimer);
      programmaticTimer = window.setTimeout(function () { programmaticTarget = -1; }, 700);
    }

    // Live sync: one read per animation frame while the track scrolls.
    function syncFromScroll() {
      rafPending = false;
      var nearest = Math.round(position() / width());
      nearest = Math.max(0, Math.min(slides.length - 1, nearest));
      if (programmaticTarget !== -1) {
        if (nearest === programmaticTarget) programmaticTarget = -1;
        return; // keep the clicked/autoplay target lit while travelling
      }
      if (nearest !== current) {
        current = nearest;
        setActiveDot(current);
      }
    }

    track.addEventListener('scroll', function () {
      if (!rafPending) {
        rafPending = true;
        window.requestAnimationFrame(syncFromScroll);
      }
    }, { passive: true });

    if ('onscrollend' in window) {
      track.addEventListener('scrollend', function () {
        programmaticTarget = -1;
        syncFromScroll();
        settle();
      });
    }

    // Autoplay
    function canAutoplay() {
      return autoplayAllowed && !prefersReducedMotion() && !document.hidden && !hovering && !dragging;
    }
    function stopTimer() {
      if (timer) { window.clearInterval(timer); timer = null; }
    }
    function startTimer() {
      stopTimer();
      if (!canAutoplay()) return;
      timer = window.setInterval(function () {
        if (canAutoplay()) goTo(current + 1);
      }, speed);
    }
    function resetTimer() {
      stopTimer();
      startTimer();
    }

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        goTo(i);
        resetTimer();
      });
    });

    root.addEventListener('mouseenter', function () { hovering = true; stopTimer(); });
    root.addEventListener('mouseleave', function () { hovering = false; resetTimer(); });
    // Touch: pause while a finger is on the slider, resume after
    track.addEventListener('touchstart', function () { stopTimer(); }, { passive: true });
    track.addEventListener('touchend', function () { resetTimer(); }, { passive: true });

    // Keyboard: arrow keys when focus is inside the slider
    root.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      var forward = (e.key === 'ArrowRight') !== isRtl;
      goTo(current + (forward ? 1 : -1));
      resetTimer();
    });

    // Mouse drag (touch scrolls natively)
    var pointerId = null;
    var startX = 0;
    var startScroll = 0;
    var startIndex = 0;
    var lastX = 0;
    var lastT = 0;
    var velocity = 0;
    var moved = false;

    if (slides.length > 1) track.style.cursor = 'grab';

    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0 || slides.length < 2) return;
      dragging = true;
      moved = false;
      pointerId = e.pointerId;
      startX = lastX = e.clientX;
      lastT = e.timeStamp;
      velocity = 0;
      startScroll = track.scrollLeft;
      startIndex = current;
      programmaticTarget = -1;
      stopTimer();
      // follow the cursor 1:1 - no smooth easing, no snapping mid-drag
      track.style.scrollBehavior = 'auto';
      track.style.scrollSnapType = 'none';
      track.style.cursor = 'grabbing';
      try { track.setPointerCapture(pointerId); } catch (err) { /* noop */ }
    });

    track.addEventListener('pointermove', function (e) {
      if (!dragging || e.pointerId !== pointerId) return;
      var dx = e.clientX - startX;
      if (Math.abs(dx) > DRAG_THRESHOLD) moved = true;
      var dt = e.timeStamp - lastT;
      if (dt > 0) velocity = (e.clientX - lastX) / dt;
      lastX = e.clientX;
      lastT = e.timeStamp;
      track.scrollLeft = startScroll - dx;
    });

    // Restore snapping and make sure we landed exactly on a slide, even if
    // the smooth settle was interrupted.
    var settlePending = false;
    var settleTimer = null;
    function settle() {
      if (!settlePending || dragging) return;
      settlePending = false;
      window.clearTimeout(settleTimer);
      track.style.scrollSnapType = '';
      if (Math.abs(position() - current * width()) > 2) scrollToIndex(current, false);
    }

    function endDrag(e) {
      if (!dragging || (e && e.pointerId !== pointerId)) return;
      dragging = false;
      pointerId = null;
      track.style.cursor = 'grab';

      var dx = lastX - startX;
      // LTR: dragging left (dx < 0) moves forward. RTL mirrors it.
      var forwardSign = isRtl ? 1 : -1;
      var target = startIndex;
      if (moved) {
        var distanceCommit = Math.abs(dx) > width() * SWIPE_RATIO;
        var flickCommit = Math.abs(velocity) > FLICK_VELOCITY;
        if (distanceCommit || flickCommit) {
          var dir = Math.sign(dx || velocity) === forwardSign ? 1 : -1;
          target = Math.max(0, Math.min(slides.length - 1, startIndex + dir));
        }
      }

      track.style.scrollBehavior = '';
      goTo(target);
      settlePending = true;
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(settle, 650); // fallback if scrollend never fires
      resetTimer();
    }

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('lostpointercapture', endDrag);

    // A real drag must not also follow the slide link
    track.addEventListener('click', function (e) {
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
        moved = false;
      }
    }, true);

    track.addEventListener('dragstart', function (e) { e.preventDefault(); });

    // Keep the current slide aligned if the viewport is resized/rotated
    var resizeTimer;
    window.addEventListener('resize', function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () { scrollToIndex(current, false); }, 100);
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stopTimer(); else resetTimer();
    });

    // Warm the remaining slides once the page has painted
    function warmImages() {
      slides.forEach(function (slide) {
        slide.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
          img.loading = 'eager';
        });
      });
    }
    if (document.readyState === 'complete') {
      window.setTimeout(warmImages, 300);
    } else {
      window.addEventListener('load', function () { window.setTimeout(warmImages, 300); });
    }

    setActiveDot(current);
    startTimer();
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-hero-slider]').forEach(initSlider);
  }

  if (document.readyState !== 'loading') initAll(document);
  else document.addEventListener('DOMContentLoaded', function () { initAll(document); });

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
